import * as R from 'ramda'
import {
  addRentalComplete,
  addTagsToRental,
  addTagsToRentalComplete,
  archiveRentalComplete,
  deleteRentalComplete,
  loadArchivedRentalsComplete,
  loadRentalComplete,
  loadRentalImages,
  loadRentalImagesComplete,
  loadRentalRatesComplete,
  loadRentalsComplete,
  loadRentalsImageCountsComplete,
  loadRentalsImagesComplete,
  loadRentalsPrimaryImages,
  loadRentalsPrimaryImagesComplete,
  loadSubdomainComplete,
  restrictUser,
  restrictUserAll,
  restrictUserAllComplete,
  restrictUserComplete,
  restrictRentalUsers,
  restrictRentalUsersComplete,
  setRentalChildren,
  setRentalChildrenComplete,
  setRentalSubUnits,
  setRentalSubUnitsComplete,
  toggleOwnerRentals,
  toggleOwnerRentalsComplete,
  unArchiveRentalComplete,
  unRestrictUser,
  unRestrictUserAll,
  unRestrictUserAllComplete,
  unRestrictUserComplete,
  updateAllRentalsCheckTimesComplete,
  updateMultipleRentalsComplete,
  updateRentalOwners,
  updateRentalOwnersComplete,
  updateRental,
  updateRentalBaseRate,
  updateRentalBaseRateComplete,
  updateRentalComplete,
  updateRentalCustomFields,
  updateRentalCustomFieldsComplete,
  updateRentalDetails,
  updateRentalDetailsComplete,
  updateRentalGPSComplete,
  updateRentalImageOrderComplete,
  updateRentalInstructions,
  updateRentalInstructionsComplete,
  updateRentalMultiUnitSettingsComplete,
  updateRentalPaymentScheduleComplete,
  UpdateRentalsStatusComplete,
  UpdateRentalStatusComplete,
  updateRentalTaxesComplete,
  addSubUnitRentalsComplete,
  UpdateSubunitRentalsComplete,
  UpdateRentalsStatus,
  UpdateSubunitRentals,
  addSubUnitRentals,
  updateRentalSecurityDepositConfig,
  syncRentalEntityTags,
} from './rental.actions'
import { adapter, initialState, State } from './rental.state'
import { Action, createReducer, on } from '@ngrx/store'
import { Rental } from './rental.models'
import * as lodash from 'lodash'
import { upsertOne, updateMany, updateOne } from '../utility/utility.adapter'
import {
  addRateMapping,
  addRateMappingComplete,
  deleteRateMappingComplete,
  deleteRateMappingsComplete,
  setRateMappingsLastSyncComplete,
  toggleRateMappingsSyncComplete,
  updateRateMappings,
  updateRateMappingsComplete,
} from './rental-rate-mapping.actions'

export const reducer = createReducer(
  initialState,
  on(loadRentalsComplete, (state, { rentals }) =>
    adapter.setAll(
      rentals.map((rental) => {
        const cachedImages = state.entities[rental.id]?.images

        return rental.images == null && cachedImages != null
          ? Object.assign(new Rental(), rental, { images: cachedImages })
          : rental
      }),
      { ...state, isLoaded: true }
    )
  ),
  on(addRentalComplete, (state, { rental }) => adapter.addOne(rental, state)),
  on(addSubUnitRentalsComplete, (state, { rentals }) => adapter.addMany(rentals, { ...state, isSavingRental: false })),
  on(loadRentalComplete, (state, { rental }) => upsertOne(adapter, rental, state)),
  on(deleteRentalComplete, (state, { id }) => adapter.removeOne(id, state)),
  on(loadSubdomainComplete, (state, { domain }) => ({ ...state, subdomain: domain })),
  on(loadRentalsImageCountsComplete, (state, { updates }) =>
    updateMany(
      adapter,
      updates,
      lodash.merge(state, {
        imageCountsLoaded: true,
      })
    )
  ),
  on(loadRentalsImagesComplete, (state, { updates }) =>
    adapter.updateMany(updates, state)
  ),
  on(
    updateRentalTaxesComplete,
    updateRentalPaymentScheduleComplete,
    updateRentalImageOrderComplete,
    updateRentalGPSComplete,
    (state, { update }) => updateOne(adapter, update, state)
  ),
  on(updateRentalComplete, updateRentalMultiUnitSettingsComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingRental: false,
      })
    )
  ),
  on(syncRentalEntityTags, (state, { updates }) => adapter.updateMany(updates, state)),
  on(updateRentalSecurityDepositConfig, (state, { rentalId, config }) =>
    updateOne(adapter, { id: rentalId, changes: { securityDepositConfig: config ?? null } }, state)
  ),
  on(UpdateRentalStatusComplete, (state, { update }) => updateOne(adapter, update, state)),
  on(
    UpdateRentalsStatusComplete,
    UpdateSubunitRentalsComplete,
    setRentalChildrenComplete,
    setRentalSubUnitsComplete,
    (state, { updates }) => updateMany(adapter, updates, { ...state, isSavingRental: false })
  ),
  on(loadRentalRatesComplete, updateAllRentalsCheckTimesComplete, updateMultipleRentalsComplete, (state, { updates }) =>
    updateMany(adapter, updates, { ...state, isRatesLoaded: true })
  ),
  on(updateRentalOwnersComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isUpdatingOwners: false,
      })
    )
  ),
  on(archiveRentalComplete, (state, { rental }) =>
    adapter.removeOne(
      rental.id,
      lodash.merge(state, {
        archivedRentals: R.append(rental, state.archivedRentals),
      })
    )
  ),
  on(unArchiveRentalComplete, (state, { rental }) =>
    adapter.upsertOne(
      rental,
      lodash.merge(state, {
        archivedRentals: R.reject((r: Rental) => r.id === rental.id, state.archivedRentals),
      })
    )
  ),
  on(loadArchivedRentalsComplete, (state, { rentals }) => ({ ...state, archivedRentals: rentals })),
  on(toggleOwnerRentalsComplete, (state, { updates }) =>
    updateMany(
      adapter,
      updates,
      lodash.merge(state, {
        isUpdatingOwners: false,
      })
    )
  ),
  on(restrictRentalUsersComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isRestrictingUsers: false,
      })
    )
  ),
  on(addTagsToRentalComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingTags: false,
      })
    )
  ),
  on(updateRentalCustomFieldsComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingCustom: false,
      })
    )
  ),
  on(updateRentalDetailsComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingDetailInfo: false,
      })
    )
  ),
  on(updateRentalInstructionsComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingRentalInstructions: false,
      })
    )
  ),
  on(updateRentalBaseRateComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isSavingBaseRate: false,
      })
    )
  ),
  on(restrictUserComplete, unRestrictUserComplete, (state, { update }) =>
    updateOne(
      adapter,
      update,
      lodash.merge(state, {
        isRestrictingUsers: false,
      })
    )
  ),
  on(restrictUserAllComplete, unRestrictUserAllComplete, (state, { updates }) =>
    updateMany(
      adapter,
      updates,
      lodash.merge(state, {
        isRestrictingUsers: false,
      })
    )
  ),
  on(loadRentalImages, (state) => ({ ...state, isImageLoaded: false })),
  on(loadRentalImagesComplete, (state, { update }) => {
    const images = R.sortBy((img) => img.order || Infinity, update.changes.images)

    return updateOne(
      adapter,
      {
        id: update.id as string,
        changes: { images },
      },
      { ...state, isImageLoaded: true }
    )
  }),
  on(loadRentalsPrimaryImages, (state) => ({ ...state, isPrimaryImagesLoaded: false })),
  on(loadRentalsPrimaryImagesComplete, (state, { updates }) =>
    updateMany(adapter, updates, { ...state, isPrimaryImagesLoaded: true })
  ),
  on(updateRentalOwners, toggleOwnerRentals, (state) => ({ ...state, isUpdatingOwners: true })),
  on(restrictUser, restrictUserAll, unRestrictUser, unRestrictUserAll, restrictRentalUsers, (state) => ({
    ...state,
    isRestrictingUsers: true,
  })),
  on(
    updateRental,
    setRentalChildren,
    setRentalSubUnits,
    UpdateRentalsStatus,
    UpdateSubunitRentals,
    addSubUnitRentals,
    UpdateSubunitRentals,
    (state) => ({
      ...state,
      isSavingRental: true,
    })
  ),
  on(addTagsToRental, (state) => ({ ...state, isSavingTags: true })),
  on(updateRentalCustomFields, (state) => ({ ...state, isSavingCustom: true })),
  on(updateRentalDetails, (state) => ({ ...state, isSavingDetailInfo: true })),
  on(updateRentalInstructions, (state) => ({ ...state, isSavingRentalInstructions: true })),
  on(updateRentalBaseRate, (state) => ({ ...state, isSavingBaseRate: true })),
  // rate mappings
  on(
    //
    addRateMappingComplete,
    deleteRateMappingComplete,
    (state, { update }) => updateOne(adapter, update, { ...state, isSavingRateMapping: false })
  ),
  on(
    //
    toggleRateMappingsSyncComplete,
    deleteRateMappingsComplete,
    setRateMappingsLastSyncComplete,
    updateRateMappingsComplete,
    (state, { updates }) => updateMany(adapter, updates, { ...state, isSavingRateMapping: false })
  ),
  on(addRateMapping, updateRateMappings, (state) => ({ ...state, isSavingRateMapping: true }))
)

export function rentalReducer(state: State | undefined, action: Action) {
  return reducer(state, action)
}

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors()
